import { Controller } from '@hotwired/stimulus'
import * as d3 from 'd3'

// connects to data-controller="ex-bubble-chart"
export default class extends Controller {
  static values = { events: String }
  static targets = ['chart', 'table']

  connect () {
    if (!this.hasEventsValue) return

    let parsed
    try {
      parsed = JSON.parse(this.eventsValue)
    } catch (e) {
      console.error('JSON parse error in bubble chart:', e)
      return
    }

    if (!Array.isArray(parsed) || parsed.length === 0) return

    const data = parsed.map((d, i) => ({
      ...d,
      dwellMinutes: (d.dwell || 0) / 60,
      _index: i
    }))

    this.render(data)
  }

  disconnect () {
    if (this.hasChartTarget) {
      this.chartTarget.innerHTML = ''
    }

    if (this.hasTableTarget) {
      this.tableTarget.innerHTML = ''
    }
  }

  render (data) {
    const colors = [
      '#e63946',
      '#457b9d',
      '#2a9d8f',
      '#f4a261',
      '#8d99ae',
      '#c77dff',
      '#06d6a0'
    ]

    const colorScale = d3
      .scaleOrdinal()
      .domain(data.map(d => d.event))
      .range(colors)

    this.renderTable(data, colorScale, this.tableTarget)
    this.renderChart(data, colorScale, this.chartTarget)
  }

  renderChart (data, colorScale, chartContainer) {
    chartContainer.innerHTML = ''
    chartContainer.style.position = 'relative'

    const baseWidth = 900
    const baseHeight = 420
    const margin = { top: 100, right: 40, bottom: 70, left: 80 }

    const innerWidth = baseWidth - margin.left - margin.right
    const innerHeight = baseHeight - margin.top - margin.bottom

    const svg = d3
      .select(chartContainer)
      .append('svg')
      .attr('viewBox', `0 0 ${baseWidth} ${baseHeight}`)
      .attr('width', '100%')
      .attr('height', '100%')
      .style('display', 'block')

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const xMin = d3.min(data, d => d.conversion)
    const xMax = d3.max(data, d => d.conversion)
    const yMin = d3.min(data, d => d.dwellMinutes)
    const yMax = d3.max(data, d => d.dwellMinutes)

    const xPad = (xMax - xMin) * 0.15
    const yPad = (yMax - yMin) * 0.2

    const x = d3
      .scaleLinear()
      .domain([xMin - xPad, xMax + xPad])
      .nice()
      .range([0, innerWidth])

    const y = d3
      .scaleLinear()
      .domain([Math.max(0, yMin - yPad), yMax + yPad])
      .nice()
      .range([innerHeight, 0])

    const r = d3
      .scaleSqrt()
      .domain(d3.extent(data, d => d.visitors))
      .range([10, 35])

    // gridlines behind bubbles
    g.append('g')
      .attr('class', 'bubble-chart-grid bubble-chart-grid-y')
      .call(
        d3.axisLeft(y)
          .ticks(5)
          .tickSize(-innerWidth)
          .tickFormat('')
      )

    g.append('g')
      .attr('class', 'bubble-chart-grid bubble-chart-grid-x')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3.axisBottom(x)
          .ticks(6)
          .tickSize(-innerHeight)
          .tickFormat('')
      )

    // axes
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x).ticks(6).tickFormat(d => `${d}%`))

    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat(d => `${d.toFixed(2)}m`))

    // axis labels
    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + 45)
      .attr('text-anchor', 'middle')
      .attr('fill', '#333')
      .text('Conversion (%)')

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -55)
      .attr('text-anchor', 'middle')
      .attr('fill', '#333')
      .text('Dwell (minutes)')

    // titles
    svg
      .append('text')
      .attr('x', baseWidth / 2)
      .attr('y', 32)
      .attr('text-anchor', 'middle')
      .attr('font-weight', '600')
      .attr('font-size', '18px')
      .text('Event comparison')

    svg
      .append('text')
      .attr('x', baseWidth / 2)
      .attr('y', 52)
      .attr('text-anchor', 'middle')
      .attr('fill', '#555')
      .attr('font-size', '12px')
      .text('Bubble size based on visitors')

    const tooltip = d3
      .select(chartContainer)
      .append('div')
      .attr('class', 'bubble-tooltip')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('padding', '8px 10px')
      .style('background', 'white')
      .style('border', '1px solid #ccc')
      .style('border-radius', '5px')
      .style('box-shadow', '0 2px 6px rgba(0,0,0,0.15)')
      .style('font-size', '12px')
      .style('opacity', 0)

    g.selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('cx', d => x(d.conversion))
      .attr('cy', d => y(d.dwellMinutes))
      .attr('r', d => r(d.visitors))
      .attr('fill', d => colorScale(d.event))
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1.5)
      .on('mouseover', (event, d) => {
        tooltip
          .style('opacity', 1)
          .html(
            `<strong>${d.event}</strong><br>` +
            `Conversion: ${d.conversion}%<br>` +
            `Dwell: ${d.dwellMinutes.toFixed(1)} min<br>` +
            `Visitors: ${d.visitors}`
          )
      })
      .on('mousemove', event => {
        const [px, py] = d3.pointer(event, chartContainer)
        tooltip
          .style('left', px + 15 + 'px')
          .style('top', py + 'px')
      })
      .on('mouseout', () => {
        tooltip.style('opacity', 0)
      })
  }

  renderTable (data, colorScale, container) {
    container.innerHTML = ''

    let html = `
      <table class="bubble-legend-table">
        <thead>
          <tr>
            <th>Color</th>
            <th>Event</th>
            <th>Conversion</th>
            <th>Dwell (min)</th>
            <th>Visitors</th>
          </tr>
        </thead>
        <tbody>
    `

    data.forEach(d => {
      html += `
        <tr>
          <td>
            <span style="
              display:inline-block;
              width:16px;height:16px;
              border-radius:3px;
              border:1px solid #ccc;
              background:${colorScale(d.event)};
            "></span>
          </td>
          <td>${d.event}</td>
          <td>${d.conversion}</td>
          <td>${d.dwellMinutes.toFixed(1)}</td>
          <td>${d.visitors}</td>
        </tr>
      `
    })

    html += '</tbody></table>'

    container.innerHTML = html
  }
}
